import { QueryParams, ResponseFormat, TAG_TYPES } from 'types';
import { baseApi } from './index';
import { InventoryDocument as T } from 'models';
import { getQueryParams } from 'utils';
import { BaseInventoryDocument, InventoryDocumentResponse } from 'models/InventoryDocument';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/inventory-documents`;
const tagType = TAG_TYPES.OrderSupplier;
const OrderSupplierApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrderSuppliers: builder.query<
      {
        data: {
          rows: T[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        if (query === '') return { url: urlEndpoint, method: 'GET' };
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),
    createOrderSupplier: builder.mutation<InventoryDocumentResponse, BaseInventoryDocument>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.PurchaseOrder, id: 'LIST' },
      ],
    }),
    updateOrderSupplier: builder.mutation<
      InventoryDocumentResponse,
      { orderSupplierId: string; data: BaseInventoryDocument; query?: QueryParams }
    >({
      query: ({ orderSupplierId, data }) => ({
        url: `${urlEndpoint}/${orderSupplierId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { orderSupplierId }) => [
        { type: tagType, id: orderSupplierId },
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.PurchaseOrder, id: 'LIST' },
      ],
    }),
    confirmOrderSupplier: builder.mutation<InventoryDocumentResponse, { orderSupplierId: string }>({
      query: ({ orderSupplierId }) => ({
        url: `${urlEndpoint}/${orderSupplierId}/confirmations`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { orderSupplierId }) => [
        { type: tagType, id: orderSupplierId },
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.PurchaseOrder, id: 'LIST' },
      ],
    }),
    getByIdOrderSupplier: builder.query<InventoryDocumentResponse, { orderSupplierId: string; query?: QueryParams }>({
      query: ({ orderSupplierId, query }) => {
        const queryString = query ? `?${getQueryParams(query)}` : '';
        return {
          url: `${urlEndpoint}/${orderSupplierId}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { orderSupplierId }) => [{ type: tagType, orderSupplierId }],
    }),
    deleteOrderSupplier: builder.mutation<ResponseFormat<T>, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
  }),
});
export const {
  useGetAllOrderSuppliersQuery: useGetOrderSuppliersQuery,
  useCreateOrderSupplierMutation,
  useConfirmOrderSupplierMutation,
  useUpdateOrderSupplierMutation,
  useGetByIdOrderSupplierQuery: useGetOrderSupplierByIdQuery,
  useDeleteOrderSupplierMutation,
  useLazyGetByIdOrderSupplierQuery,
} = OrderSupplierApiService;
